/**
 * @fileoverview Illustrated help panel for the data-report builder — thin wrapper
 *             around the generic HelpPanel with the report help content registry.
 * @module ui-admin/components/ReportBuilderHelpPanel
 *
 * Responsibilities: - Present report-builder help sections (canvas, parameters,
 *                     scheduled delivery, run/preview, library) via the shared
 *                     HelpPanel — same UX as the document builder's help.
 * Dependencies:     - ../HelpPanel (generic panel), ../../help/illustrations,
 *                     ../../help/reportBuilderHelpContent (section registry).
 * Architecture:     - Presentation-only; static bundled content (local-first —
 *                     available offline; no Dexie/API).
 * Notes:            - Mirrors BuilderHelpPanel's shape so the two builders share
 *                     the identical help surface (DRY — new content file only).
 */

import { HelpPanel } from '../HelpPanel/HelpPanel';
import { ILLUSTRATIONS } from '../../help/illustrations';
import { REPORT_BUILDER_HELP_SECTIONS, type ReportHelpSectionId } from '../../help/reportBuilderHelpContent';
import type { IllustrationId } from '../../help/types';

/**
 * @contract ReportBuilderHelpPanelProps
 * Purpose: Props for the report-builder help panel.
 * Fields:
 * - open: whether the panel is shown.
 * - onClose: called on ×, Escape, or backdrop click.
 * - onStartTour: optional; called by "Take the tour →" (closes panel, starts tour).
 * - initialSectionId: optional section to open at.
 */
export interface ReportBuilderHelpPanelProps {
  open: boolean;
  onClose: () => void;
  onStartTour?: () => void;
  initialSectionId?: ReportHelpSectionId;
}

/**
 * @component ReportBuilderHelpPanel
 * Purpose:     Full-screen illustrated help for the data-report builder.
 * Props:       ReportBuilderHelpPanelProps.
 * Behavior:    Delegates to the generic HelpPanel with the report registry.
 * Data Source: static help content (no API/Dexie).
 * Constraints: no mock data; no network.
 */
export function ReportBuilderHelpPanel({ open, onClose, onStartTour, initialSectionId }: ReportBuilderHelpPanelProps) {
  return (
    <HelpPanel<ReportHelpSectionId, IllustrationId>
      open={open}
      onClose={onClose}
      onStartTour={onStartTour}
      initialSectionId={initialSectionId}
      sections={REPORT_BUILDER_HELP_SECTIONS}
      illustrations={ILLUSTRATIONS}
      tocTitle="Report Builder Help"
      heading="How the report builder works"
      ariaLabel="Report builder help"
      panelTestId="report-builder-help-panel"
    />
  );
}

export default ReportBuilderHelpPanel;
